import React from 'react';

// Define props interface for GermanBrandsSlider
interface GermanBrandsSliderProps {
  language?: string;
}

// Define translations for GermanBrandsSlider content with subheadline split into two lines
const TRANSLATIONS: Record<string, {
  headline: string;
  subheadlineLine1: string;
  subheadlineLine2: string;
}> = {
  en: {
    headline: "Our Domains of Global Network",
    subheadlineLine1: "Explore the worldwide presence of Made in Germany",
    subheadlineLine2: "through our strategically established domain network."
  },
  de: {
    headline: "Unsere Domains des globalen Netzwerks",
    subheadlineLine1: "Erfahren Sie mehr über die weltweite Präsenz von Made in Germany",
    subheadlineLine2: "durch unser strategisch aufgebautes Domain-Netzwerk."
  },
  es: {
    headline: "Nuestros dominios de la red global",
    subheadlineLine1: "Explore la presencia mundial de Made in Germany",
    subheadlineLine2: "a través de nuestra red de dominios estratégicamente establecida."
  },
  fr: {
    headline: "Nos domaines du réseau mondial",
    subheadlineLine1: "Découvrez la présence mondiale de Made in Germany",
    subheadlineLine2: "grâce à notre réseau de domaines stratégiquement établi."
  },
  it: {
    headline: "I nostri domini della rete globale",
    subheadlineLine1: "Esplora la presenza mondiale di Made in Germany",
    subheadlineLine2: "attraverso la nostra rete di domini strategicamente stabilita."
  },
  nl: {
    headline: "Onze domeinen van het wereldwijde netwerk",
    subheadlineLine1: "Ontdek de wereldwijde aanwezigheid van Made in Germany",
    subheadlineLine2: "via ons strategisch opgebouwde domeinnetwerk."
  },
  sa: {
    headline: "مجالاتنا في الشبكة العالمية",
    subheadlineLine1: "استكشف الوجود العالمي لـ Made in Germany",
    subheadlineLine2: "من خلال شبكة المجالات التي أنشأناها استراتيجيًا."
  },
  hk: {
    headline: "我們的全球網絡域名",
    subheadlineLine1: "探索 Made in Germany 的全球影響力",
    subheadlineLine2: "通過我們策略性建立的域名網絡。"
  },
  sg: {
    headline: "我们的全球网络域名",
    subheadlineLine1: "探索 Made in Germany 的全球影响力",
    subheadlineLine2: "通过我们策略性建立的域名网络。"
  },
  za: {
    headline: "Ons domeine van die wêreldwye netwerk",
    subheadlineLine1: "Ontdek die wêreldwye teenwoordigheid van Made in Germany",
    subheadlineLine2: "deur ons strategies opgestelde domeinnetwerk."
  },
  kr: {
    headline: "우리의 글로벌 네트워크 도메인",
    subheadlineLine1: "Made in Germany의 전 세계적 입지를 알아보세요",
    subheadlineLine2: "전략적으로 구축된 도메인 네트워크를 통해."
  },
  jp: {
    headline: "我々のグローバルネットワークのドメイン",
    subheadlineLine1: "Made in Germanyの世界的な存在感をご覧ください",
    subheadlineLine2: "戦略的に構築されたドメインネットワークを通じて。"
  },
};

const GermanBrandsSlider: React.FC<GermanBrandsSliderProps> = ({ language = 'en' }) => {
  // List of domains (unchanged as per your request)
  const domains = [
    "made-in-germany.uk",
    "made-in-germany.world",
    "made-in-germany.global",
    "made-in-germany-first.com",
    "made-in-germany-arab.com",
    "made-in-germany-arabia.com",
    "made-in-germany.africa",
    "made-in-germany.asia",
    "made-in-germany-china.com",
    "made-in-germany.my",
    "made-in-germany.com.in",
    "made-in-germany.co.in",
  ];

  const translations = TRANSLATIONS[language] || TRANSLATIONS['en'];

  return (
    <div className="bg-[#0B111F] rounded-2xl w-full max-w-7xl mx-auto mt-10">
      {/* Domains Section */}
      <div className="max-w-6xl mx-auto py-9 pb-8">
        <h3 className="text-3xl font-bold text-white text-center mb-4">
          {translations.headline}
        </h3>
        <p className="text-lg text-gray-300 text-center mb-8">
          {translations.subheadlineLine1}
          <br />
          {translations.subheadlineLine2}
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 mb-10">
          {domains.map((domain, index) => (
            <div
              key={index}
              className="bg-[#1A1F2E] p-4 rounded-lg border border-[#2A2F3E] 
                         transform transition-all duration-300 hover:scale-105 
                         hover:bg-[#2A2F3E] hover:border-blue-500 flex items-center 
                         justify-center shadow-md"
            >
              <a
                href={`https://${domain}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-white text-sm font-medium hover:text-blue-400 
                           transition-colors duration-200 truncate"
              >
                {domain}
              </a>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default GermanBrandsSlider;